/**
 * Lumio page context: a compact text outline of what the user currently sees,
 * captured at message-send time and sent with every chat turn (the panel lives
 * in the layout and outlives navigation, so context must ride per turn, never
 * per conversation).
 *
 * Why an outline and not raw HTML or innerText: styled-components class names
 * carry no meaning (60 KB of noise per page), and flat text loses which number
 * belongs to which card. The serializer walks the rendered DOM from `<main>` —
 * which structurally excludes the nav, the top bar, and the Lumio panel itself —
 * and emits indented lines that preserve grouping while dropping markup.
 *
 * Rules (each earned against real owner-hub pages):
 *  - skip aria-hidden subtrees (nav clones, carousel duplicates), svg, media,
 *    style/script/template
 *  - <number-flow-react> renders its digits in shadow DOM (invisible to text
 *    extraction), but exposes the value in its `data` attribute — read that
 *  - h1..h6, section[aria-label] and [data-lumio-heading] become headings
 *  - [role=progressbar] becomes "label: N%"
 *  - buttons become "[button: X]", carrying aria-pressed selection state;
 *    disabled buttons are dropped
 *  - role=button elements with data-id (campaign/score cards) and
 *    [data-lumio-group] components group their children with the id preserved
 *  - elements with short visible text merge to a single line; consecutive
 *    duplicate lines collapse to "line (×N)"
 *  - email addresses and phone numbers are masked (see `redact`): owner-hub
 *    pages render the operator's players, whose personal data must not leave
 *    the browser
 *  - a [data-lumio-private] subtree is skipped entirely, for a field whose
 *    format the mask cannot rely on (see `isPrivate`)
 *  - some routes are never captured at all (see `snapshotSuppressed`): where a
 *    whole record describes an identifiable person, masking its identifier is
 *    not enough
 *  - the whole snapshot is capped — the backend rejects oversized payloads
 *
 * The snapshot is display context only: the backend prompt frames it as "what
 * the user is looking at", never as a source of facts or actions.
 */
import { store } from '../../store';
import { routeLabel } from '../layouts/MainLayout/routeLabels';
import { redact, snapshotSuppressed } from './redact';

/** Envelope for the backend's `context` request field (see Lumio PageContext). */
export interface LumioPageContext {
  page: string;
  section: string;
  casino_instance_id: string;
  screen: string;
}

/**
 * The envelope `/chat/init` actually reads.
 *
 * Init uses `section` so its greeting can name the page the owner is on, and
 * discards the screen outline it parses. Sending the outline there shipped a
 * second full snapshot of the page, personal data included, on the first turn
 * of every conversation, for nothing.
 */
export type LumioInitContext = Omit<LumioPageContext, 'screen'>;

/** Strip the snapshot from an envelope bound for `/chat/init`. */
export const initPageContext = (ctx: LumioPageContext | null): LumioInitContext | null =>
  ctx ? { page: ctx.page, section: ctx.section, casino_instance_id: ctx.casino_instance_id } : null;

const SCREEN_CAP = 3000;
const LEAF_MAX = 80;
const CARD_MIN_DESCENDANTS = 15;

const SKIP_TAGS = new Set([
  'svg',
  'style',
  'script',
  'template',
  'noscript',
  'iframe',
  'canvas',
  'img',
  'video',
  'audio',
  'link',
  'meta',
]);

const isHidden = (el: Element): boolean => el.getAttribute('aria-hidden') === 'true';

/**
 * Opt a subtree out of the snapshot entirely.
 *
 * `redact` masks by pattern, which only works where the shape is predictable.
 * Some fields are not: the linked-Telegram number in Settings is stored exactly
 * as the provider's OIDC claim delivers it, with no normalization anywhere in
 * the chain, so it may arrive with or without a country prefix. Marking the
 * element is exact where a regex would be a guess, and it cannot mistake an id
 * or a revenue figure for personal data the way a looser pattern would.
 */
const isPrivate = (el: Element): boolean => el.hasAttribute('data-lumio-private');

/** The numeric value of a <number-flow-react>, read from its data attribute. */
const numberFlowValue = (el: Element): string => {
  try {
    const data = JSON.parse(el.getAttribute('data') ?? '') as { valueAsString?: string; value?: number };
    return String(data.valueAsString ?? data.value ?? '');
  } catch {
    return '';
  }
};

/** Visible text of a subtree: skips hidden/skipped nodes, resolves number-flow values. */
const inlineText = (el: Element): string => {
  let out = '';
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      out += `${node.textContent ?? ''} `;
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const child = node as Element;
    const tag = child.tagName.toLowerCase();
    if (SKIP_TAGS.has(tag) || isHidden(child) || isPrivate(child)) continue;
    out += `${tag === 'number-flow-react' ? numberFlowValue(child) : inlineText(child)} `;
  }
  return out.replace(/\s+/g, ' ').trim();
};

const walk = (el: Element, depth: number, lines: string[]): void => {
  const tag = el.tagName.toLowerCase();
  if (SKIP_TAGS.has(tag) || isHidden(el) || isPrivate(el)) return;
  const pad = '  '.repeat(depth);

  const heading = /^h([1-6])$/.exec(tag);
  if (heading) {
    lines.push('', `${pad}${'#'.repeat(Number(heading[1]))} ${inlineText(el)}`);
    return;
  }
  if (el.hasAttribute('data-lumio-heading')) {
    lines.push('', `${pad}## ${inlineText(el)}`);
    return;
  }
  if (tag === 'number-flow-react') {
    lines.push(pad + numberFlowValue(el));
    return;
  }

  const role = el.getAttribute('role');
  const label = el.getAttribute('aria-label');

  if (role === 'progressbar') {
    lines.push(`${pad}${label ?? 'progress'}: ${el.getAttribute('aria-valuenow') ?? '?'}%`);
    return;
  }

  const isButton = tag === 'button' || role === 'button';
  const isCard =
    (isButton && (el.hasAttribute('data-id') || el.querySelectorAll('*').length > CARD_MIN_DESCENDANTS)) ||
    el.hasAttribute('data-lumio-group');

  if (isButton && !isCard) {
    if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return;
    const pressed = el.getAttribute('aria-pressed') === 'true' ? ' — selected' : '';
    const text = inlineText(el) || label || '';
    if (text) lines.push(`${pad}[button: ${text}${pressed}]`);
    return;
  }

  if (tag === 'section' && label) lines.push('', `${pad}## ${label}`);

  let childDepth = depth;
  if (isCard) {
    const id = el.getAttribute('data-id');
    const name = label ?? el.getAttribute('data-lumio-group') ?? id ?? 'item';
    lines.push(`${pad}- ${name}${id ? ` (id: ${id})` : ''}`);
    childDepth = depth + 1;
  }

  // Short-text leaf: merge the whole element into one line, unless it contains
  // structure that deserves its own lines.
  const text = inlineText(el);
  if (
    text &&
    text.length <= LEAF_MAX &&
    !el.querySelector(
      'h1,h2,h3,h4,h5,h6,button,[role=button],[role=progressbar],table,[data-id],[data-lumio-group],[data-lumio-heading]',
    )
  ) {
    lines.push('  '.repeat(childDepth) + text);
    return;
  }
  for (const child of Array.from(el.children)) walk(child, childDepth, lines);
};

/** Collapse duplicate lines, trim blank runs, and cap the total size. */
const postProcess = (lines: string[]): string => {
  const out: string[] = [];
  let prev: string | null = null;
  let count = 0;
  const flush = (): void => {
    if (count > 1) out[out.length - 1] += ` (×${count})`;
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line === prev && line !== '') {
      count += 1;
      continue;
    }
    flush();
    if (!(line === '' && out[out.length - 1] === '')) out.push(line);
    prev = line;
    count = 1;
  }
  flush();
  // Redact on the assembled outline, so one pass covers card names, aria-labels
  // and leaf text alike, and do it before the cap so truncation can never split
  // an address into a still-readable fragment.
  let text = redact(out.join('\n').trim());
  if (text.length > SCREEN_CAP) text = `${text.slice(0, SCREEN_CAP)}\n… [truncated]`;
  return text;
};

/** Serialize an element subtree to the outline format. Exported for tests. */
export const serializeElement = (root: Element): string => {
  const lines: string[] = [];
  walk(root, 0, lines);
  return postProcess(lines);
};

/**
 * Capture the current page as a Lumio context envelope, or `null` when there is
 * nothing to send.
 * Reads the active casino from the store directly — no Provider needed, matching
 * the chat transport's redux-free style.
 */
export const buildLumioPageContext = (): LumioPageContext | null => {
  const main = document.querySelector('main');
  if (!main) return null;
  const pathname = window.location.pathname;
  // A suppressed route still reports WHERE the owner is, so the assistant can
  // name the page; only the contents of the screen are withheld.
  const screen = snapshotSuppressed(pathname) ? '' : serializeElement(main);
  return {
    page: pathname,
    section: routeLabel(pathname),
    casino_instance_id: store.getState().casinoInstance?.displayBrand?.id ?? '',
    screen,
  };
};
